import L from "leaflet";
import * as turf from "@turf/turf";

const STATE_KEY = "hydrotools:mbswath-planner:v1";
const MAX_LINES = 500;
const MIN_SPACING_M = 5;
const MIN_LINE_LENGTH_M = 10;

function esc(v) {
  return String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function defaultState() {
  return {
    azimuth: 0,
    swathAngle: 120,
    overlap: 10,
    minDepth: 5,
    mode: "azimuth",
    boundaryCoords: null,
    spineCoords: null,
    sectionCoords: null,
    sectionSampleSpacing: 25,
  };
}

function readState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== "object") return defaultState();
    const s = { ...defaultState(), ...parsed };
    s.azimuth = Math.max(0, Math.min(359, Number(s.azimuth) || 0));
    s.swathAngle = Math.max(1, Math.min(179, Number(s.swathAngle) || 120));
    s.overlap = Math.max(0, Math.min(95, Number(s.overlap) || 10));
    s.minDepth = Math.max(0.5, Number(s.minDepth) || 5);
    s.mode = ["azimuth", "spine", "section"].includes(s.mode) ? s.mode : "azimuth";
    s.boundaryCoords = Array.isArray(s.boundaryCoords) && s.boundaryCoords.length >= 3 ? s.boundaryCoords : null;
    s.spineCoords = Array.isArray(s.spineCoords) && s.spineCoords.length >= 2 ? s.spineCoords : null;
    s.sectionCoords = Array.isArray(s.sectionCoords) && s.sectionCoords.length >= 2 ? s.sectionCoords : null;
    s.sectionSampleSpacing = Math.max(1, Math.min(5000, Number(s.sectionSampleSpacing) || 25));
    return s;
  } catch {
    return defaultState();
  }
}

function persistState(s) {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify({
      azimuth: s.azimuth, swathAngle: s.swathAngle, overlap: s.overlap,
      minDepth: s.minDepth, mode: s.mode,
      boundaryCoords: s.boundaryCoords, spineCoords: s.spineCoords,
      sectionCoords: s.sectionCoords, sectionSampleSpacing: s.sectionSampleSpacing,
    }));
  } catch {}
}

function projectOntoAxis(pt, origin, bearingDeg) {
  const dist = turf.distance(origin, pt, { units: "meters" });
  if (dist < 0.0001) return 0;
  const bear = turf.bearing(origin, pt);
  return dist * Math.cos(((bear - bearingDeg) * Math.PI) / 180);
}

function clipLineToPoly(lineCoords, polygon) {
  const line = turf.lineString(lineCoords);
  let polyOutline;
  try { polyOutline = turf.polygonToLine(polygon); } catch { return null; }
  let result;
  try { result = turf.lineIntersect(line, polyOutline); } catch { return null; }
  const pts = result?.features ?? [];
  if (pts.length < 2) {
    const mid = [(lineCoords[0][0] + lineCoords[1][0]) / 2, (lineCoords[0][1] + lineCoords[1][1]) / 2];
    try { if (turf.booleanPointInPolygon(turf.point(mid), polygon)) return lineCoords; } catch {}
    return null;
  }
  const startPt = turf.point(lineCoords[0]);
  pts.sort((a, b) => turf.distance(startPt, a, { units: "meters" }) - turf.distance(startPt, b, { units: "meters" }));
  return [pts[0].geometry.coordinates, pts[pts.length - 1].geometry.coordinates];
}

// Extend both ends of a polyline outward along its tangent by distM meters.
function extendPolylineEnds(coords, distM) {
  if (coords.length < 2) return coords;
  const km = distM / 1000;
  const startBear = turf.bearing(turf.point(coords[1]), turf.point(coords[0]));
  const newStart = turf.destination(turf.point(coords[0]), km, startBear, { units: "kilometers" }).geometry.coordinates;
  const n = coords.length;
  const endBear = turf.bearing(turf.point(coords[n - 2]), turf.point(coords[n - 1]));
  const newEnd = turf.destination(turf.point(coords[n - 1]), km, endBear, { units: "kilometers" }).geometry.coordinates;
  return [newStart, ...coords, newEnd];
}

// Snap a point just inside to the exact polygon edge using the adjacent outside point.
function snapToPolygonEdge(insidePt, outsidePt, polygon) {
  try {
    const polyOutline = turf.polygonToLine(polygon);
    const ints = turf.lineIntersect(turf.lineString([outsidePt, insidePt]), polyOutline);
    if (ints.features.length > 0) return ints.features[0].geometry.coordinates;
  } catch {}
  return insidePt;
}

// Clip a multi-segment polyline to a polygon: densify to ~40m steps, find the
// largest contiguous inside-polygon run, snap endpoints to exact boundary crossing.
function clipPolylineToBoundary(coords, polygon) {
  if (!coords || coords.length < 2) return null;
  const dense = [];
  for (let i = 0; i < coords.length - 1; i++) {
    const [x0, y0] = coords[i], [x1, y1] = coords[i + 1];
    const dx = x1 - x0, dy = y1 - y0;
    const steps = Math.max(2, Math.ceil(Math.sqrt(dx * dx + dy * dy) / 0.00036));
    for (let s = 0; s < steps; s++) dense.push([x0 + dx * (s / steps), y0 + dy * (s / steps)]);
  }
  dense.push(coords[coords.length - 1]);
  const inside = dense.map((c) => { try { return turf.booleanPointInPolygon(turf.point(c), polygon); } catch { return false; } });
  let bestStart = -1, bestLen = 0, curStart = -1, curLen = 0;
  for (let i = 0; i < inside.length; i++) {
    if (inside[i]) {
      if (curStart === -1) curStart = i;
      curLen++;
      if (curLen > bestLen) { bestLen = curLen; bestStart = curStart; }
    } else { curStart = -1; curLen = 0; }
  }
  if (bestLen < 2) return null;
  const result = dense.slice(bestStart, bestStart + bestLen);
  // Snap endpoints to exact polygon boundary crossing
  if (bestStart > 0) result[0] = snapToPolygonEdge(dense[bestStart], dense[bestStart - 1], polygon);
  const endIdx = bestStart + bestLen - 1;
  if (endIdx + 1 < dense.length) result[result.length - 1] = snapToPolygonEdge(dense[endIdx], dense[endIdx + 1], polygon);
  return result;
}

// Like clipPolylineToBoundary but returns ALL inside-polygon runs, not just the largest.
// This catches water areas separated by land (e.g. inner harbour behind a peninsula).
function clipPolylineToPolygonAll(coords, polygon) {
  if (!coords || coords.length < 2) return [];
  const dense = [];
  for (let i = 0; i < coords.length - 1; i++) {
    const [x0, y0] = coords[i], [x1, y1] = coords[i + 1];
    const dx = x1 - x0, dy = y1 - y0;
    const steps = Math.max(2, Math.ceil(Math.sqrt(dx * dx + dy * dy) / 0.00036));
    for (let s = 0; s < steps; s++) dense.push([x0 + dx * (s / steps), y0 + dy * (s / steps)]);
  }
  dense.push(coords[coords.length - 1]);
  const inside = dense.map((c) => { try { return turf.booleanPointInPolygon(turf.point(c), polygon); } catch { return false; } });
  const runs = [];
  let curStart = -1;
  for (let i = 0; i <= inside.length; i++) {
    if (i < inside.length && inside[i]) { if (curStart === -1) curStart = i; }
    else if (curStart !== -1) {
      if (i - curStart >= 2) {
        const run = dense.slice(curStart, i);
        if (curStart > 0) run[0] = snapToPolygonEdge(dense[curStart], dense[curStart - 1], polygon);
        if (i < dense.length) run[run.length - 1] = snapToPolygonEdge(dense[i - 1], dense[i], polygon);
        runs.push(run);
      }
      curStart = -1;
    }
  }
  return runs;
}

function azimuthLabel(deg) {
  const d = ((deg % 360) + 360) % 360;
  const labels = ["N","NNE","NNE","NE","NE","ENE","ENE","E","E","ESE","ESE","SE","SE","SSE","SSE","S","S","SSW","SSW","SW","SW","WSW","WSW","W","W","WNW","WNW","NW","NW","NNW","NNW","N"];
  return labels[Math.round(d / 11.25) % 32];
}

function previewCalc(swathAngle, minDepth, overlap) {
  const swath = 2 * minDepth * Math.tan((swathAngle / 2) * Math.PI / 180);
  const spacing = swath * (1 - overlap / 100);
  return { swath: Math.round(swath), spacing: Math.round(spacing) };
}

function buildSpineSegments(spineCoords) {
  const segments = [];
  let cumLen = 0;
  for (let i = 0; i < spineCoords.length - 1; i++) {
    const p1 = turf.point(spineCoords[i]);
    const p2 = turf.point(spineCoords[i + 1]);
    const len = turf.distance(p1, p2, { units: "meters" });
    const bearing = turf.bearing(p1, p2);
    segments.push({ start: spineCoords[i], end: spineCoords[i + 1], bearing, length: len, cumStart: cumLen });
    cumLen += len;
  }
  return { segments, totalLength: cumLen };
}

function getSpinePositionAt(segments, totalLength, s) {
  const clamped = Math.max(0, Math.min(totalLength, s));
  for (const seg of segments) {
    if (clamped <= seg.cumStart + seg.length + 1e-6) {
      const t = seg.length > 0 ? (clamped - seg.cumStart) / seg.length : 0;
      const pt = turf.destination(turf.point(seg.start), t * seg.length, seg.bearing, { units: "meters" });
      return { point: pt.geometry.coordinates, bearing: seg.bearing };
    }
  }
  const last = segments[segments.length - 1];
  return { point: last.end, bearing: last.bearing };
}

function createRuntime(app) {
  const map = app.map.instance;
  const mapEl = app.map.el;

  const boundaryLayer = L.layerGroup().addTo(map);
  const spineLayer = L.layerGroup().addTo(map);
  const sectionLayer = L.layerGroup().addTo(map);
  const linesLayer = L.layerGroup().addTo(map);
  const drawLayer = L.layerGroup().addTo(map);
  const contourLayer = L.layerGroup().addTo(map);

  let state = readState();
  let containerEl = null;

  let drawingBoundary = false, drawVertices = [], drawHandlers = [];
  let drawingAzimuth = false, azimuthPt1 = null, azimuthDrawHandlers = [];
  let drawingSpine = false, spineVertices = [], spineDrawHandlers = [];
  let drawingSection = false, sectionVertices = [], sectionDrawHandlers = [];

  let generating = false, generateAbort = false;
  let contourDrawn = false;
  let waterGrid = null, waterGridBbox = null, waterGridN = 0;
  let lines = [], stats = null, status = "";

  function persist() { persistState(state); }
  function setStatus(s) {
    status = s;
    const el = containerEl?.querySelector?.("#mbslp-status");
    if (el) el.textContent = s;
  }

  // ── Map display ───────────────────────────────────────────────────────────────

  function renderBoundaryOnMap() {
    boundaryLayer.clearLayers();
    if (!state.boundaryCoords || state.boundaryCoords.length < 3) return;
    L.polygon(state.boundaryCoords.map(([lng, lat]) => [lat, lng]), {
      color: "#f59e0b", weight: 2, fillColor: "#f59e0b", fillOpacity: 0.06, dashArray: "6 4", interactive: false,
    }).addTo(boundaryLayer);
  }

  function renderSpineOnMap() {
    spineLayer.clearLayers();
    if (!state.spineCoords || state.spineCoords.length < 2) return;
    const ll = state.spineCoords.map(([lng, lat]) => [lat, lng]);
    L.polyline(ll, { color: "#a78bfa", weight: 2.5, dashArray: "8 4", interactive: false }).addTo(spineLayer);
    ll.forEach((p) => L.circleMarker(p, { radius: 4, color: "#a78bfa", fillColor: "#a78bfa", fillOpacity: 1, interactive: false }).addTo(spineLayer));
  }

  function renderSectionOnMap() {
    sectionLayer.clearLayers();
    if (!state.sectionCoords || state.sectionCoords.length < 2) return;
    const ll = state.sectionCoords.map(([lng, lat]) => [lat, lng]);
    L.polyline(ll, { color: "#fb7185", weight: 2.5, dashArray: "8 4", interactive: false }).addTo(sectionLayer);
    ll.forEach((p) => L.circleMarker(p, { radius: 4, color: "#fb7185", fillColor: "#fb7185", fillOpacity: 1, interactive: false }).addTo(sectionLayer));
  }

  function renderLinesOnMap() {
    linesLayer.clearLayers();
    lines.forEach((f) => {
      const coords = f.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
      const p = f.properties;
      L.polyline(coords, { color: "#22d3ee", weight: 1.8, opacity: 0.85, interactive: false })
        .addTo(linesLayer)
        .bindTooltip(`${p.name} | d:${p.waterDepthM}m swath:${p.swathWidthM}m spacing:${p.spacingM}m`,
          { permanent: false, direction: "top", sticky: true, opacity: 0.9 });
    });
  }

  // ── Boundary draw ─────────────────────────────────────────────────────────────

  function drawKeyHandler(e) {
    if (e.key === "Escape") stopDraw("cancelled");
    if (e.key === "Enter") finishDraw();
  }
  function updateDrawPreview(mouseLatLng) {
    drawLayer.clearLayers();
    const pts = mouseLatLng ? [...drawVertices, [mouseLatLng.lng, mouseLatLng.lat]] : drawVertices;
    if (!pts.length) return;
    const ll = pts.map(([lng, lat]) => [lat, lng]);
    if (pts.length >= 2) L.polyline(ll, { color: "#f59e0b", weight: 2, dashArray: "5 5", interactive: false }).addTo(drawLayer);
    if (pts.length >= 3) L.polygon(ll, { color: "#f59e0b", weight: 1, fillOpacity: 0.08, interactive: false }).addTo(drawLayer);
    ll.forEach((p) => L.circleMarker(p, { radius: 4, color: "#f59e0b", fillOpacity: 1, interactive: false }).addTo(drawLayer));
  }
  function finishDraw() {
    if (drawVertices.length < 3) { app.ui.toast("Need at least 3 points."); return; }
    state.boundaryCoords = [...drawVertices];
    lines = []; stats = null; waterGrid = null;
    contourLayer.clearLayers(); contourDrawn = false;
    persist(); stopDraw("completed"); renderBoundaryOnMap();
    app.ui.toast(`Boundary set — ${state.boundaryCoords.length} vertices.`);
  }
  function stopDraw(reason) {
    drawingBoundary = false;
    drawHandlers.forEach(([evt, fn]) => { try { map.off(evt, fn); } catch {} });
    document.removeEventListener("keydown", drawKeyHandler);
    drawHandlers = []; drawLayer.clearLayers(); drawVertices = [];
    if (mapEl) mapEl.style.cursor = "";
    render();
  }
  function startDrawBoundary() {
    if (drawingBoundary) { finishDraw(); return; }
    if (drawingAzimuth) stopDrawAzimuth("cancelled");
    if (drawingSpine) stopSpineDraw("cancelled");
    if (drawingSection) stopSectionDraw("cancelled");
    state.boundaryCoords = null; lines = []; stats = null; status = "";
    boundaryLayer.clearLayers(); linesLayer.clearLayers();
    drawingBoundary = true; drawVertices = [];
    if (mapEl) mapEl.style.cursor = "crosshair";
    render();
    app.ui.toast("Click to add vertices · Right-click or Enter to finish · Esc to cancel");
    const onClick = (e) => {
      const last = drawVertices[drawVertices.length - 1];
      if (last && Math.abs(last[0] - e.latlng.lng) < 1e-9 && Math.abs(last[1] - e.latlng.lat) < 1e-9) return;
      drawVertices.push([e.latlng.lng, e.latlng.lat]);
      updateDrawPreview(null);
      const el = containerEl?.querySelector?.("#mbslp-draw-count");
      if (el) el.textContent = `${drawVertices.length} pts`;
    };
    const onMove = (e) => updateDrawPreview(e.latlng);
    const onDbl = (e) => { L.DomEvent.stop(e); finishDraw(); };
    const onCtx = (e) => { L.DomEvent.stop(e); finishDraw(); };
    map.on("click", onClick); map.on("mousemove", onMove); map.on("dblclick", onDbl); map.on("contextmenu", onCtx);
    document.addEventListener("keydown", drawKeyHandler);
    drawHandlers = [["click", onClick], ["mousemove", onMove], ["dblclick", onDbl], ["contextmenu", onCtx]];
  }

  // ── Azimuth draw ──────────────────────────────────────────────────────────────

  function azimuthKeyHandler(e) { if (e.key === "Escape") stopDrawAzimuth("cancelled"); }
  function updateAzimuthPreview(mouseLatLng) {
    drawLayer.clearLayers();
    if (!azimuthPt1) return;
    const pt1LL = [azimuthPt1[1], azimuthPt1[0]];
    L.polyline([pt1LL, [mouseLatLng.lat, mouseLatLng.lng]], { color: "#38bdf8", weight: 2, dashArray: "6 4", interactive: false }).addTo(drawLayer);
    L.circleMarker(pt1LL, { radius: 5, color: "#38bdf8", fillColor: "#38bdf8", fillOpacity: 1, interactive: false }).addTo(drawLayer);
    const az = Math.round(((turf.bearing(turf.point(azimuthPt1), turf.point([mouseLatLng.lng, mouseLatLng.lat])) % 360) + 360) % 360);
    const el = containerEl?.querySelector?.("#mbslp-az-live");
    if (el) el.textContent = `${az}° — click to confirm`;
  }
  function stopDrawAzimuth(reason) {
    drawingAzimuth = false; azimuthPt1 = null;
    azimuthDrawHandlers.forEach(([evt, fn]) => { try { map.off(evt, fn); } catch {} });
    document.removeEventListener("keydown", azimuthKeyHandler);
    azimuthDrawHandlers = []; drawLayer.clearLayers();
    if (mapEl) mapEl.style.cursor = "";
    render();
  }
  function startDrawAzimuth() {
    if (drawingAzimuth) { stopDrawAzimuth("cancelled"); return; }
    if (drawingBoundary) stopDraw("cancelled");
    if (drawingSpine) stopSpineDraw("cancelled");
    if (drawingSection) stopSectionDraw("cancelled");
    drawingAzimuth = true; azimuthPt1 = null;
    if (mapEl) mapEl.style.cursor = "crosshair";
    render();
    app.ui.toast("Click first point · click second point to set azimuth · Esc to cancel");
    const onClick = (e) => {
      if (!azimuthPt1) { azimuthPt1 = [e.latlng.lng, e.latlng.lat]; updateAzimuthPreview(e.latlng); }
      else {
        const bearing = turf.bearing(turf.point(azimuthPt1), turf.point([e.latlng.lng, e.latlng.lat]));
        state.azimuth = Math.round(((bearing % 360) + 360) % 360);
        persist(); stopDrawAzimuth("completed");
        app.ui.toast(`Azimuth set to ${state.azimuth}° (${azimuthLabel(state.azimuth)})`);
      }
    };
    const onMove = (e) => { if (azimuthPt1) updateAzimuthPreview(e.latlng); };
    const onCtx = (e) => { L.DomEvent.stop(e); stopDrawAzimuth("cancelled"); };
    map.on("click", onClick); map.on("mousemove", onMove); map.on("contextmenu", onCtx);
    document.addEventListener("keydown", azimuthKeyHandler);
    azimuthDrawHandlers = [["click", onClick], ["mousemove", onMove], ["contextmenu", onCtx]];
  }

  // ── Spine draw ────────────────────────────────────────────────────────────────

  function spineKeyHandler(e) {
    if (e.key === "Escape") stopSpineDraw("cancelled");
    if (e.key === "Enter") finishSpineDraw();
  }
  function updateSpinePreview(mouseLatLng) {
    drawLayer.clearLayers();
    const pts = mouseLatLng ? [...spineVertices, [mouseLatLng.lng, mouseLatLng.lat]] : spineVertices;
    if (!pts.length) return;
    const ll = pts.map(([lng, lat]) => [lat, lng]);
    if (pts.length >= 2) L.polyline(ll, { color: "#a78bfa", weight: 2.5, dashArray: "6 4", interactive: false }).addTo(drawLayer);
    ll.forEach((p) => L.circleMarker(p, { radius: 4, color: "#a78bfa", fillColor: "#a78bfa", fillOpacity: 1, interactive: false }).addTo(drawLayer));
  }
  function finishSpineDraw() {
    if (spineVertices.length < 2) { app.ui.toast("Need at least 2 points for a spine."); return; }
    state.spineCoords = [...spineVertices];
    lines = []; stats = null;
    persist(); stopSpineDraw("completed"); renderSpineOnMap();
    app.ui.toast(`Spine set — ${state.spineCoords.length} vertices.`);
  }
  function stopSpineDraw(reason) {
    drawingSpine = false;
    spineDrawHandlers.forEach(([evt, fn]) => { try { map.off(evt, fn); } catch {} });
    document.removeEventListener("keydown", spineKeyHandler);
    spineDrawHandlers = []; drawLayer.clearLayers(); spineVertices = [];
    if (mapEl) mapEl.style.cursor = "";
    render();
  }
  function startDrawSpine() {
    if (drawingSpine) { finishSpineDraw(); return; }
    if (drawingBoundary) stopDraw("cancelled");
    if (drawingAzimuth) stopDrawAzimuth("cancelled");
    if (drawingSection) stopSectionDraw("cancelled");
    state.spineCoords = null; lines = []; stats = null; status = "";
    spineLayer.clearLayers(); linesLayer.clearLayers();
    drawingSpine = true; spineVertices = [];
    if (mapEl) mapEl.style.cursor = "crosshair";
    render();
    app.ui.toast("Click to add spine points · Right-click or Enter to finish · Esc to cancel");
    const onClick = (e) => {
      const last = spineVertices[spineVertices.length - 1];
      if (last && Math.abs(last[0] - e.latlng.lng) < 1e-9 && Math.abs(last[1] - e.latlng.lat) < 1e-9) return;
      spineVertices.push([e.latlng.lng, e.latlng.lat]);
      updateSpinePreview(null);
      const el = containerEl?.querySelector?.("#mbslp-spine-count");
      if (el) el.textContent = `${spineVertices.length} pts`;
    };
    const onMove = (e) => updateSpinePreview(e.latlng);
    const onDbl = (e) => { L.DomEvent.stop(e); finishSpineDraw(); };
    const onCtx = (e) => { L.DomEvent.stop(e); finishSpineDraw(); };
    map.on("click", onClick); map.on("mousemove", onMove); map.on("dblclick", onDbl); map.on("contextmenu", onCtx);
    document.addEventListener("keydown", spineKeyHandler);
    spineDrawHandlers = [["click", onClick], ["mousemove", onMove], ["dblclick", onDbl], ["contextmenu", onCtx]];
  }

  // ── Section draw ──────────────────────────────────────────────────────────────

  function sectionKeyHandler(e) {
    if (e.key === "Escape") stopSectionDraw("cancelled");
    if (e.key === "Enter") finishSectionDraw();
  }
  function updateSectionPreview(mouseLatLng) {
    drawLayer.clearLayers();
    const pts = mouseLatLng ? [...sectionVertices, [mouseLatLng.lng, mouseLatLng.lat]] : sectionVertices;
    if (!pts.length) return;
    const ll = pts.map(([lng, lat]) => [lat, lng]);
    if (pts.length >= 2) L.polyline(ll, { color: "#fb7185", weight: 2.5, dashArray: "6 4", interactive: false }).addTo(drawLayer);
    ll.forEach((p) => L.circleMarker(p, { radius: 4, color: "#fb7185", fillColor: "#fb7185", fillOpacity: 1, interactive: false }).addTo(drawLayer));
  }
  function finishSectionDraw() {
    if (sectionVertices.length < 2) { app.ui.toast("Need at least 2 points for a section."); return; }
    state.sectionCoords = [...sectionVertices];
    lines = []; stats = null;
    persist(); stopSectionDraw("completed"); renderSectionOnMap();
    app.ui.toast(`Section set — ${state.sectionCoords.length} vertices.`);
  }
  function stopSectionDraw(reason) {
    drawingSection = false;
    sectionDrawHandlers.forEach(([evt, fn]) => { try { map.off(evt, fn); } catch {} });
    document.removeEventListener("keydown", sectionKeyHandler);
    sectionDrawHandlers = []; drawLayer.clearLayers(); sectionVertices = [];
    if (mapEl) mapEl.style.cursor = "";
    render();
  }
  function startDrawSection() {
    if (drawingSection) { finishSectionDraw(); return; }
    if (drawingBoundary) stopDraw("cancelled");
    if (drawingAzimuth) stopDrawAzimuth("cancelled");
    if (drawingSpine) stopSpineDraw("cancelled");
    state.sectionCoords = null; lines = []; stats = null; status = "";
    sectionLayer.clearLayers(); linesLayer.clearLayers();
    drawingSection = true; sectionVertices = [];
    if (mapEl) mapEl.style.cursor = "crosshair";
    render();
    app.ui.toast("Click to add section points · Right-click or Enter to finish · Esc to cancel");
    const onClick = (e) => {
      const last = sectionVertices[sectionVertices.length - 1];
      if (last && Math.abs(last[0] - e.latlng.lng) < 1e-9 && Math.abs(last[1] - e.latlng.lat) < 1e-9) return;
      sectionVertices.push([e.latlng.lng, e.latlng.lat]);
      updateSectionPreview(null);
      const el = containerEl?.querySelector?.("#mbslp-section-count");
      if (el) el.textContent = `${sectionVertices.length} pts`;
    };
    const onMove = (e) => updateSectionPreview(e.latlng);
    const onDbl = (e) => { L.DomEvent.stop(e); finishSectionDraw(); };
    const onCtx = (e) => { L.DomEvent.stop(e); finishSectionDraw(); };
    map.on("click", onClick); map.on("mousemove", onMove); map.on("dblclick", onDbl); map.on("contextmenu", onCtx);
    document.addEventListener("keydown", sectionKeyHandler);
    sectionDrawHandlers = [["click", onClick], ["mousemove", onMove], ["dblclick", onDbl], ["contextmenu", onCtx]];
  }

  // ── File import ───────────────────────────────────────────────────────────────

  function parseKMLCoords(text) {
    return (text || "").trim().split(/\s+/).reduce((acc, triplet) => {
      const parts = triplet.split(",").map(Number);
      if (parts.length >= 2 && Number.isFinite(parts[0]) && Number.isFinite(parts[1])) acc.push([parts[0], parts[1]]);
      return acc;
    }, []);
  }
  function extractCoordsFromKML(kmlText) {
    const doc = new DOMParser().parseFromString(kmlText, "text/xml");
    for (const sel of ["outerBoundaryIs coordinates", "LinearRing coordinates", "Polygon coordinates", "coordinates"]) {
      for (const el of doc.querySelectorAll(sel)) {
        const coords = parseKMLCoords(el.textContent || "");
        if (coords.length >= 3) return coords;
      }
    }
    return null;
  }
  function extractCoordsFromGeoJSON(gj) {
    const candidates = gj.type === "FeatureCollection" ? (gj.features || []) : [gj];
    for (const item of candidates) {
      const geom = item?.geometry || item;
      if (geom?.type === "Polygon" && Array.isArray(geom.coordinates?.[0]))
        return geom.coordinates[0].map(([lon, lat]) => [lon, lat]);
      if (geom?.type === "MultiPolygon" && Array.isArray(geom.coordinates?.[0]?.[0]))
        return geom.coordinates[0][0].map(([lon, lat]) => [lon, lat]);
    }
    return null;
  }
  async function importBoundaryFile(file) {
    if (!file) return;
    const name = String(file.name || "").toLowerCase();
    try {
      let coords = null;
      if (name.endsWith(".kmz")) {
        const JSZip = (await import("jszip")).default;
        const zip = await JSZip.loadAsync(file);
        const kmlEntry = Object.values(zip.files).find((f) => f.name.toLowerCase().endsWith(".kml"));
        if (!kmlEntry) throw new Error("No KML file found inside KMZ.");
        coords = extractCoordsFromKML(await kmlEntry.async("text"));
      } else if (name.endsWith(".kml")) {
        coords = extractCoordsFromKML(await file.text());
      } else if (name.endsWith(".geojson") || name.endsWith(".json")) {
        coords = extractCoordsFromGeoJSON(JSON.parse(await file.text()));
      } else {
        app.ui.toast("Unsupported format — use KMZ, KML, or GeoJSON."); return;
      }
      if (!coords || coords.length < 3) { app.ui.toast("No valid polygon found in file."); return; }
      state.boundaryCoords = coords; lines = []; stats = null; status = "";
      waterGrid = null; contourLayer.clearLayers(); contourDrawn = false;
      persist(); renderBoundaryOnMap(); linesLayer.clearLayers(); render();
      try {
        const lats = coords.map(([, lat]) => lat), lons = coords.map(([lon]) => lon);
        map.fitBounds([[Math.min(...lats), Math.min(...lons)], [Math.max(...lats), Math.max(...lons)]], { padding: [40, 40] });
      } catch {}
      app.ui.toast(`Boundary loaded — ${coords.length} vertices.`);
    } catch (err) {
      app.ui.toast(`Import failed: ${err?.message || "Unknown error"}`);
      console.error("[MBSwathPlanner] importBoundaryFile failed", err);
    }
  }

  // ── Auto-orient ───────────────────────────────────────────────────────────────

  async function autoOrientFromBathymetry() {
    if (!state.boundaryCoords || state.boundaryCoords.length < 3) { app.ui.toast("Draw a boundary first."); return; }
    setStatus("Sampling bathymetry for auto-orient…"); render();
    try {
      const closedCoords = [...state.boundaryCoords, state.boundaryCoords[0]];
      const polygon = turf.polygon([closedCoords]);
      const bbox = turf.bbox(polygon);
      const N = 10;
      const lonStep = (bbox[2] - bbox[0]) / (N - 1);
      const latStep = (bbox[3] - bbox[1]) / (N - 1);

      const gridPts = [];
      for (let j = 0; j < N; j++) {
        for (let i = 0; i < N; i++) {
          const lon = bbox[0] + i * lonStep, lat = bbox[1] + j * latStep;
          try { if (turf.booleanPointInPolygon(turf.point([lon, lat]), polygon)) gridPts.push({ i, j, lon, lat }); } catch {}
        }
      }
      if (!gridPts.length) { setStatus(""); render(); app.ui.toast("No sample points inside boundary."); return; }

      const depths = {};
      await Promise.allSettled(gridPts.map(async ({ i, j, lon, lat }) => {
        try {
          const result = await app.services.identify.sampleDepth(lon, lat, "etopo");
          const raw = Number(result?.depth_m);
          if (Number.isFinite(raw) && raw < 0) depths[`${i},${j}`] = Math.abs(raw);
        } catch {}
      }));

      const cellWidthM = lonStep * 111320, cellHeightM = latStep * 110540;
      let gxSum = 0, gySum = 0, count = 0;
      gridPts.forEach(({ i, j }) => {
        const here = depths[`${i},${j}`];
        if (here === undefined) return;
        const east = depths[`${i+1},${j}`], west = depths[`${i-1},${j}`];
        const north = depths[`${i},${j+1}`], south = depths[`${i},${j-1}`];
        if (east !== undefined && west !== undefined) { gxSum += (east - west) / (2 * cellWidthM); count++; }
        if (north !== undefined && south !== undefined) { gySum += (north - south) / (2 * cellHeightM); count++; }
      });

      if (!count) { setStatus(""); render(); app.ui.toast("Insufficient bathymetry data for auto-orient."); return; }
      const gradBearing = Math.atan2(gxSum / count, gySum / count) * (180 / Math.PI);
      state.azimuth = Math.round(((gradBearing + 90) % 360 + 360) % 360);
      persist();
      setStatus(`Auto-orient: ${state.azimuth}° (${azimuthLabel(state.azimuth)}) — along-isobath from ${count} gradient samples.`);
      render();
    } catch (err) {
      setStatus("Auto-orient failed.");
      console.error("[MBSwathPlanner] autoOrient failed", err);
    }
  }

  // ── Line generation ───────────────────────────────────────────────────────────

  async function sampleEtopo(lon, lat, minDepth) {
    let waterDepthM = minDepth, isLand = false;
    try {
      const result = await app.services.identify.sampleDepth(lon, lat, "etopo");
      const raw = Number(result?.depth_m);
      if (Number.isFinite(raw)) {
        if (raw >= 0) isLand = true;
        else waterDepthM = Math.max(minDepth, Math.abs(raw));
      }
    } catch {}
    return { waterDepthM, isLand };
  }

  async function sampleSectionProfile(segments, totalLength, sampleSpacingM, minDepth) {
    const spacing = Math.max(1, Number(sampleSpacingM) || 25);
    const sampleCount = Math.min(2000, Math.max(2, Math.floor(totalLength / spacing) + 1));
    const positions = [];
    for (let i = 0; i < sampleCount; i++) positions.push(Math.min(totalLength, i * spacing));
    if (positions[positions.length - 1] < totalLength - 0.001) positions.push(totalLength);

    const samples = [];
    for (let i = 0; i < positions.length && !generateAbort; i++) {
      const chainageM = positions[i];
      const { point, bearing } = getSpinePositionAt(segments, totalLength, chainageM);
      setStatus(`Sampling section ${i + 1}/${positions.length}…`);
      const { waterDepthM, isLand } = await sampleEtopo(point[0], point[1], minDepth);
      samples.push({ chainageM, point, bearing, waterDepthM, isLand });
    }
    return samples;
  }

  function depthAtSectionChainage(samples, chainageM, minDepth) {
    if (!samples.length) return { waterDepthM: minDepth, isLand: false };
    if (chainageM <= samples[0].chainageM) return { waterDepthM: samples[0].waterDepthM, isLand: samples[0].isLand };
    for (let i = 0; i < samples.length - 1; i++) {
      const a = samples[i], b = samples[i + 1];
      if (chainageM <= b.chainageM + 1e-6) {
        const span = Math.max(1e-6, b.chainageM - a.chainageM);
        const t = Math.max(0, Math.min(1, (chainageM - a.chainageM) / span));
        return {
          waterDepthM: Math.max(minDepth, a.waterDepthM + (b.waterDepthM - a.waterDepthM) * t),
          isLand: a.isLand && b.isLand,
        };
      }
    }
    const last = samples[samples.length - 1];
    return { waterDepthM: last.waterDepthM, isLand: last.isLand };
  }

  async function generateLines() {
    if (generating) { generateAbort = true; return; }
    if (!state.boundaryCoords || state.boundaryCoords.length < 3) { app.ui.toast("Draw a survey boundary first."); return; }
    if (state.mode === "spine" && (!state.spineCoords || state.spineCoords.length < 2)) { app.ui.toast("Draw a spine line first."); return; }
    if (state.mode === "section" && (!state.sectionCoords || state.sectionCoords.length < 2)) { app.ui.toast("Draw a section line first."); return; }

    generating = true; generateAbort = false; lines = []; stats = null;
    linesLayer.clearLayers(); render();

    try {
      setStatus("Building minimum-depth grid…"); render();
      await buildWaterGrid();

      const closedCoords = [...state.boundaryCoords, state.boundaryCoords[0]];
      const polygon = turf.polygon([closedCoords]);
      const halfSwathRad = (state.swathAngle / 2) * Math.PI / 180;
      const overlapFrac = Math.max(0, Math.min(0.95, state.overlap / 100));
      const minDepth = Math.max(0.5, state.minDepth);
      const bbox = turf.bbox(polygon);
      const diagonalM = turf.distance(turf.point([bbox[0], bbox[1]]), turf.point([bbox[2], bbox[3]]), { units: "meters" });
      const longLineLen = diagonalM * 1.5 + 2000;

      const generated = [], depthsSampled = [], spacingsSampled = [];

      if (state.mode === "spine") {
        // Corridor mode: offset the spine outward on both sides using turf.lineOffset.
        // Lines run PARALLEL to the spine (following its curvature) — non-crossing by definition.
        const spineLine = turf.lineString(state.spineCoords);
        const defaultSpacing = Math.max(MIN_SPACING_M, 2 * minDepth * Math.tan(halfSwathRad) * (1 - overlapFrac));

        const buildSide = async (direction, startOffset) => {
          const sideLines = [];
          let cumOffset = startOffset, prevSpacing = defaultSpacing, misses = 0, dryRunM = 0;
          while (sideLines.length < MAX_LINES / 2 && !generateAbort && misses < 4) {
            let offsetLine;
            try { offsetLine = cumOffset === 0 ? spineLine : turf.lineOffset(spineLine, direction * cumOffset, { units: "meters" }); }
            catch { break; }
            const extendedCoords = extendPolylineEnds(offsetLine.geometry.coordinates, longLineLen);

            // Get ALL inside-polygon runs at this offset (handles peninsulas / inner harbours)
            const polyRuns = clipPolylineToPolygonAll(extendedCoords, polygon);
            if (polyRuns.length === 0) {
              misses++; dryRunM = 0; cumOffset += prevSpacing; continue;
            }
            misses = 0;

            let addedAny = false;
            for (const polyClipped of polyRuns) {
              const clippedRuns = waterGrid ? clipLineToDepthRuns(polyClipped, minDepth) : [polyClipped];
              for (const clippedCoords of clippedRuns) {
                if (!clippedCoords || clippedCoords.length < 2) continue;
                const lineLen = lineLengthM(clippedCoords);
                if (lineLen < MIN_LINE_LENGTH_M) continue;
                const midCoord = clippedCoords[Math.floor(clippedCoords.length / 2)];
                setStatus(`Offset ${direction >= 0 ? "+" : "−"}${Math.round(cumOffset)}m: querying depth…`);
                const { waterDepthM, isLand } = await sampleEtopo(midCoord[0], midCoord[1], minDepth);
                if (isLand) continue;
                const swathWidth = 2 * waterDepthM * Math.tan(halfSwathRad);
                const spacing = Math.max(MIN_SPACING_M, swathWidth * (1 - overlapFrac));
                prevSpacing = spacing;
                depthsSampled.push(waterDepthM); spacingsSampled.push(spacing);
                sideLines.push({ clippedCoords, waterDepthM, swathWidth, spacing, lineLen });
                addedAny = true;
              }
            }

            if (addedAny) {
              dryRunM = 0; cumOffset += prevSpacing;
            } else {
              // In polygon but all land — small step; stop if dry run exceeds polygon diagonal
              const step = Math.max(10, prevSpacing * 0.15);
              dryRunM += step; cumOffset += step;
              if (dryRunM > diagonalM) break;
            }
          }
          return sideLines;
        };

        // Right side starts at 0 (includes the spine itself), left side starts past spine
        const rightLines = await buildSide(1, 0);
        const initLeftSpacing = rightLines.length > 0 ? rightLines[0].spacing : defaultSpacing;
        const leftLines = await buildSide(-1, initLeftSpacing);

        // Order: far-left … near-left … spine … near-right … far-right
        const ordered = [...leftLines.reverse(), ...rightLines];
        ordered.forEach(({ clippedCoords, waterDepthM, swathWidth, spacing, lineLen }, i) => {
          const lineNum = i + 1;
          generated.push({
            type: "Feature",
            properties: {
              name: `L${String(lineNum).padStart(3, "0")}`, lineIndex: lineNum,
              waterDepthM: Math.round(waterDepthM * 10) / 10,
              swathWidthM: Math.round(swathWidth * 10) / 10,
              spacingM: Math.round(spacing * 10) / 10,
              lengthM: Math.round(lineLen * 10) / 10,
            },
            geometry: { type: "LineString", coordinates: clippedCoords },
          });
        });
      } else if (state.mode === "section") {
        const { segments, totalLength } = buildSpineSegments(state.sectionCoords);
        if (!segments.length || totalLength < 1) { setStatus("Section is too short."); return; }
        const samples = await sampleSectionProfile(segments, totalLength, state.sectionSampleSpacing, minDepth);
        if (generateAbort) { setStatus("Cancelled."); return; }
        const wetSamples = samples.filter((x) => !x.isLand);
        if (!wetSamples.length) { setStatus("No water depths found along section."); return; }

        let chainageM = 0;
        while (chainageM <= totalLength + 0.001 && generated.length < MAX_LINES && !generateAbort) {
          const { point, bearing } = getSpinePositionAt(segments, totalLength, chainageM);
          const { waterDepthM, isLand } = depthAtSectionChainage(samples, chainageM, minDepth);
          const swathWidth = 2 * waterDepthM * Math.tan(halfSwathRad);
          const spacing = Math.max(MIN_SPACING_M, swathWidth * (1 - overlapFrac));

          if (isLand) { chainageM += Math.max(5, spacing * 0.25); continue; }

          const lineBearing = (bearing + 90) % 360;
          const ls = turf.destination(turf.point(point), -longLineLen, lineBearing, { units: "meters" });
          const le = turf.destination(turf.point(point), longLineLen, lineBearing, { units: "meters" });
          const polyClipped = clipLineToPoly([ls.geometry.coordinates, le.geometry.coordinates], polygon);
          if (!polyClipped) { chainageM += Math.max(5, spacing * 0.25); continue; }

          const clippedRuns = waterGrid ? clipLineToDepthRuns(polyClipped, minDepth) : [polyClipped];
          let addedAtChainage = false;
          for (const clipped of clippedRuns) {
            if (!clipped || clipped.length < 2) continue;
            const lineLen = lineLengthM(clipped);
            if (lineLen < MIN_LINE_LENGTH_M) continue;

            depthsSampled.push(waterDepthM); spacingsSampled.push(spacing);
            const lineNum = generated.length + 1;
            generated.push({
              type: "Feature",
              properties: {
                name: `L${String(lineNum).padStart(3, "0")}`, lineIndex: lineNum,
                sectionChainageM: Math.round(chainageM * 10) / 10,
                waterDepthM: Math.round(waterDepthM * 10) / 10,
                swathWidthM: Math.round(swathWidth * 10) / 10,
                spacingM: Math.round(spacing * 10) / 10,
                lengthM: Math.round(lineLen * 10) / 10,
              },
              geometry: { type: "LineString", coordinates: clipped },
            });
            addedAtChainage = true;
          }
          if (!addedAtChainage) { chainageM += Math.max(5, spacing * 0.25); continue; }
          chainageM += spacing;
        }
      } else {
        const azimuth = ((state.azimuth % 360) + 360) % 360;
        const crosstrackBearing = (azimuth + 90) % 360;
        const centroid = turf.centroid(polygon);
        let minCT = Infinity, maxCT = -Infinity;
        turf.coordAll(polygon).forEach((coord) => {
          const proj = projectOntoAxis(turf.point(coord), centroid, crosstrackBearing);
          if (proj < minCT) minCT = proj;
          if (proj > maxCT) maxCT = proj;
        });

        let ctOffset = minCT;
        let prevSpacing = Math.max(MIN_SPACING_M, 2 * minDepth * Math.tan(halfSwathRad) * (1 - overlapFrac));

        while (ctOffset <= maxCT + prevSpacing && generated.length < MAX_LINES && !generateAbort) {
          const lineOrigin = turf.destination(centroid, ctOffset, crosstrackBearing, { units: "meters" });
          const ls = turf.destination(lineOrigin, -longLineLen, azimuth, { units: "meters" });
          const le = turf.destination(lineOrigin, longLineLen, azimuth, { units: "meters" });
          const polyClipped = clipLineToPoly([ls.geometry.coordinates, le.geometry.coordinates], polygon);

          if (!polyClipped) {
            if (ctOffset > maxCT) break;
            ctOffset += Math.max(10, prevSpacing * 0.15); continue;
          }
          const clippedRuns = waterGrid ? clipLineToDepthRuns(polyClipped, minDepth) : [polyClipped];
          let nextSpacing = prevSpacing;
          let addedAtOffset = false;
          for (const clipped of clippedRuns) {
            if (!clipped || clipped.length < 2) continue;
            const lineLen = lineLengthM(clipped);
            if (lineLen < MIN_LINE_LENGTH_M) continue;

            const midCoord = clipped[Math.floor(clipped.length / 2)];
            setStatus(`Line ${generated.length + 1}: querying depth…`);
            const { waterDepthM, isLand } = await sampleEtopo(midCoord[0], midCoord[1], minDepth);
            if (isLand) continue;

            const swathWidth = 2 * waterDepthM * Math.tan(halfSwathRad);
            const spacing = Math.max(MIN_SPACING_M, swathWidth * (1 - overlapFrac));
            nextSpacing = addedAtOffset ? Math.min(nextSpacing, spacing) : spacing;
            depthsSampled.push(waterDepthM); spacingsSampled.push(spacing);

            const lineNum = generated.length + 1;
            generated.push({
              type: "Feature",
              properties: {
                name: `L${String(lineNum).padStart(3, "0")}`, lineIndex: lineNum,
                ctOffsetM: Math.round(ctOffset * 10) / 10,
                waterDepthM: Math.round(waterDepthM * 10) / 10,
                swathWidthM: Math.round(swathWidth * 10) / 10,
                spacingM: Math.round(spacing * 10) / 10,
                lengthM: Math.round(lineLen * 10) / 10,
              },
              geometry: { type: "LineString", coordinates: clipped },
            });
            addedAtOffset = true;
          }
          if (!addedAtOffset) { ctOffset += Math.max(10, prevSpacing * 0.15); continue; }
          prevSpacing = nextSpacing;
          ctOffset += prevSpacing;
        }
      }

      if (generateAbort) { setStatus("Cancelled."); return; }

      lines = generated;
      if (lines.length > 0) {
        const totalKm = lines.reduce((s, f) => s + f.properties.lengthM, 0) / 1000;
        const avgSpacing = spacingsSampled.reduce((a, b) => a + b, 0) / spacingsSampled.length;
        stats = {
          count: lines.length, totalKm: Math.round(totalKm * 100) / 100,
          avgSpacingM: Math.round(avgSpacing),
          minSpacingM: Math.round(Math.min(...spacingsSampled)),
          maxSpacingM: Math.round(Math.max(...spacingsSampled)),
          minDepthM: Math.round(Math.min(...depthsSampled) * 10) / 10,
          maxDepthM: Math.round(Math.max(...depthsSampled) * 10) / 10,
        };
        setStatus(`Generated ${lines.length} line${lines.length === 1 ? "" : "s"}.`);
        renderLinesOnMap();
      } else {
        setStatus("No lines fit within the boundary.");
      }
    } catch (err) {
      setStatus(`Error: ${err?.message || "Unknown error"}`);
      console.error("[MBSwathPlanner] generateLines failed", err);
    } finally {
      generating = false; generateAbort = false; render();
    }
  }

  // ── Water grid (shared by contour drawing and line clipping) ─────────────────

  async function buildWaterGrid() {
    if (!state.boundaryCoords || state.boundaryCoords.length < 3) return;
    const bbox = turf.bbox(turf.polygon([[...state.boundaryCoords, state.boundaryCoords[0]]]));
    const padX = (bbox[2] - bbox[0]) * 0.15, padY = (bbox[3] - bbox[1]) * 0.15;
    const b = [bbox[0] - padX, bbox[1] - padY, bbox[2] + padX, bbox[3] + padY];
    const N = 50;
    const lonStep = (b[2] - b[0]) / (N - 1), latStep = (b[3] - b[1]) / (N - 1);
    const grid = new Array(N * N).fill(null);
    await Promise.allSettled(Array.from({ length: N * N }, (_, idx) => {
      const i = idx % N, j = Math.floor(idx / N);
      return app.services.identify.sampleDepth(b[0] + i * lonStep, b[1] + j * latStep, "etopo")
        .then((r) => { const v = Number(r?.depth_m); if (Number.isFinite(v)) grid[j * N + i] = v; })
        .catch(() => {});
    }));
    waterGrid = grid; waterGridBbox = b; waterGridN = N;
  }

  function sampleGridDepth(lon, lat) {
    if (!waterGrid || !waterGridBbox) return null;
    const [bx0, by0, bx1, by1] = waterGridBbox;
    const N = waterGridN;
    const fx = (lon - bx0) / (bx1 - bx0) * (N - 1);
    const fy = (lat - by0) / (by1 - by0) * (N - 1);
    if (fx < 0 || fx > N - 1 || fy < 0 || fy > N - 1) return null;
    const x0 = Math.floor(fx), y0 = Math.floor(fy);
    const x1 = Math.min(N - 1, x0 + 1), y1 = Math.min(N - 1, y0 + 1);
    const tx = fx - x0, ty = fy - y0;
    const v00 = waterGrid[y0 * N + x0], v10 = waterGrid[y0 * N + x1];
    const v01 = waterGrid[y1 * N + x0], v11 = waterGrid[y1 * N + x1];
    if ([v00, v10, v01, v11].every(Number.isFinite)) {
      const a = v00 * (1 - tx) + v10 * tx;
      const b = v01 * (1 - tx) + v11 * tx;
      return a * (1 - ty) + b * ty;
    }
    const ix = Math.round(fx), iy = Math.round(fy);
    const nearest = waterGrid[iy * N + ix];
    return Number.isFinite(nearest) ? nearest : null;
  }

  function isDepthPoint(lon, lat, minDepth) {
    const v = sampleGridDepth(lon, lat);
    return v === null || v <= -Math.max(0.5, Number(minDepth) || 0.5);
  }

  function contourCrossing(a, b, minDepth) {
    const va = sampleGridDepth(a[0], a[1]);
    const vb = sampleGridDepth(b[0], b[1]);
    if (!Number.isFinite(va) || !Number.isFinite(vb) || Math.abs(vb - va) < 1e-9) return null;
    const level = -Math.max(0.5, Number(minDepth) || 0.5);
    const t = Math.max(0, Math.min(1, (level - va) / (vb - va)));
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  }

  function lineLengthM(coords) {
    let total = 0;
    for (let i = 0; i < coords.length - 1; i++) total += turf.distance(turf.point(coords[i]), turf.point(coords[i + 1]), { units: "meters" });
    return total;
  }

  function clipLineToDepthRuns(coords, minDepth) {
    if (!waterGrid) return [coords];
    const stepDeg = Math.min(waterGridBbox[2] - waterGridBbox[0], waterGridBbox[3] - waterGridBbox[1]) / (waterGridN - 1) * 0.35;
    const dense = [];
    for (let i = 0; i < coords.length - 1; i++) {
      const [x0, y0] = coords[i], [x1, y1] = coords[i + 1];
      const dx = x1 - x0, dy = y1 - y0;
      const steps = Math.max(2, Math.ceil(Math.sqrt(dx * dx + dy * dy) / Math.max(stepDeg, 1e-9)));
      for (let s = 0; s < steps; s++) dense.push([x0 + dx * s / steps, y0 + dy * s / steps]);
    }
    dense.push(coords[coords.length - 1]);

    const ok = dense.map(([lon, lat]) => isDepthPoint(lon, lat, minDepth));
    const runs = [];
    let run = [];
    for (let i = 0; i < dense.length; i++) {
      if (ok[i]) {
        if (!run.length && i > 0 && !ok[i - 1]) {
          const x = contourCrossing(dense[i - 1], dense[i], minDepth);
          if (x) run.push(x);
        }
        run.push(dense[i]);
      } else if (run.length) {
        const x = i > 0 ? contourCrossing(dense[i - 1], dense[i], minDepth) : null;
        if (x) run.push(x);
        if (run.length >= 2) runs.push(run);
        run = [];
      }
    }
    if (run.length >= 2) runs.push(run);
    return runs;
  }

  // ── Minimum-depth contour ─────────────────────────────────────────────────────

  async function draw0mContour() {
    if (!state.boundaryCoords || state.boundaryCoords.length < 3) { app.ui.toast("Draw a boundary first."); return; }
    if (contourDrawn) {
      contourLayer.clearLayers(); contourDrawn = false; render(); return;
    }
    const minDepth = Math.max(0.5, Number(state.minDepth) || 0.5);
    const level = -minDepth;
    setStatus(`Sampling ETOPO for ${minDepth}m contour…`); render();
    try {
      if (!waterGrid) await buildWaterGrid();
      const b = waterGridBbox, N = waterGridN;
      const lonStep = (b[2] - b[0]) / (N - 1), latStep = (b[3] - b[1]) / (N - 1);
      const grid = waterGrid;

      // Marching squares lookup: corners BL/BR/TR/TL, 1=deep enough, 0=too shallow/land
      // bits: 0=BL 1=BR 2=TR 3=TL
      const MS = [
        [],                      // 0
        [["B","L"]],             // 1  BL
        [["B","R"]],             // 2  BR
        [["L","R"]],             // 3  BL+BR
        [["R","T"]],             // 4  TR
        [["B","L"],["R","T"]],   // 5  BL+TR saddle
        [["B","T"]],             // 6  BR+TR
        [["L","T"]],             // 7  BL+BR+TR
        [["L","T"]],             // 8  TL
        [["B","T"]],             // 9  BL+TL
        [["B","R"],["L","T"]],   // 10 BR+TL saddle
        [["R","T"]],             // 11 BL+BR+TL
        [["L","R"]],             // 12 TR+TL
        [["B","R"]],             // 13 BL+TR+TL
        [["B","L"]],             // 14 BR+TR+TL
        [],                      // 15
      ];
      const lerp = (va, vb, a, b2) => {
        const t = Math.abs(vb - va) < 1e-10 ? 0.5 : (level - va) / (vb - va);
        return [a[0] + t * (b2[0] - a[0]), a[1] + t * (b2[1] - a[1])];
      };
      const segments = [];
      for (let j = 0; j < N - 1; j++) {
        for (let i = 0; i < N - 1; i++) {
          const v00 = grid[j * N + i], v10 = grid[j * N + i + 1];
          const v11 = grid[(j+1) * N + i + 1], v01 = grid[(j+1) * N + i];
          if (v00 === null || v10 === null || v11 === null || v01 === null) continue;
          const code = (v00<=level?1:0) | (v10<=level?2:0) | (v11<=level?4:0) | (v01<=level?8:0);
          if (code === 0 || code === 15) continue;
          const pBL = [b[0]+i*lonStep, b[1]+j*latStep], pBR = [b[0]+(i+1)*lonStep, b[1]+j*latStep];
          const pTR = [b[0]+(i+1)*lonStep, b[1]+(j+1)*latStep], pTL = [b[0]+i*lonStep, b[1]+(j+1)*latStep];
          const pts = { B: lerp(v00,v10,pBL,pBR), R: lerp(v10,v11,pBR,pTR), T: lerp(v01,v11,pTL,pTR), L: lerp(v00,v01,pBL,pTL) };
          for (const [a, c] of MS[code]) segments.push([pts[a], pts[c]]);
        }
      }
      contourLayer.clearLayers();
      segments.forEach(([p1, p2]) => L.polyline([[p1[1],p1[0]],[p2[1],p2[0]]], { color:"#4ade80", weight:1.5, opacity:0.9, interactive:false }).addTo(contourLayer));
      contourDrawn = segments.length > 0;
      setStatus(contourDrawn ? `${minDepth}m contour: ${segments.length} segments.` : `No ${minDepth}m contour crossings found in boundary area.`);
      render();
    } catch (err) {
      setStatus("Contour error."); console.error("[MBSwathPlanner] draw0mContour failed", err);
    }
  }

  // ── Exports ───────────────────────────────────────────────────────────────────

  function dl(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 300);
  }

  function exportGeoJson() {
    if (!lines.length) return;
    dl(new Blob([JSON.stringify({
      type: "FeatureCollection", features: lines,
      properties: { mode: state.mode, azimuth: state.azimuth, swathAngle: state.swathAngle, overlap: state.overlap, generated: new Date().toISOString() },
    }, null, 2)], { type: "application/geo+json" }), `mbswath_lines_${Date.now()}.geojson`);
  }

  function exportHypack() {
    if (!lines.length) return;
    const out = [`LNS ${lines.length}`];
    lines.forEach((f) => {
      const coords = f.geometry.coordinates;
      out.push(`LIN ${coords.length}`);
      coords.forEach(([lon, lat]) => out.push(`PTS ${lon.toFixed(8)} ${lat.toFixed(8)}`));
      out.push(`LNN ${f.properties.name}`); out.push("EOL");
    });
    dl(new Blob([`${out.join("\n")}\n`], { type: "text/plain" }), `mbswath_lines_${Date.now()}.lnw`);
  }

  async function exportKmz() {
    if (!lines.length) return;
    const JSZip = (await import("jszip")).default;
    const placemarks = lines.map((f) => {
      const p = f.properties;
      const coords = f.geometry.coordinates.map(([lon, lat]) => `${lon},${lat},0`).join(" ");
      return `  <Placemark><name>${esc(p.name)}</name>
    <description>Depth: ${p.waterDepthM}m | Swath: ${p.swathWidthM}m | Spacing: ${p.spacingM}m | Length: ${p.lengthM}m</description>
    <Style><LineStyle><color>ffeed322</color><width>2</width></LineStyle></Style>
    <LineString><tessellate>1</tessellate><coordinates>${coords}</coordinates></LineString>
  </Placemark>`;
    }).join("\n");
    const kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2"><Document>
  <name>MBSWATH Survey Lines</name>
  <description>Mode: ${state.mode} · Swath ${state.swathAngle}° · Overlap ${state.overlap}%</description>
${placemarks}
</Document></kml>`;
    const zip = new JSZip();
    zip.file("survey_lines.kml", kml);
    dl(await zip.generateAsync({ type: "blob" }), `mbswath_lines_${Date.now()}.kmz`);
  }

  // ── UI ────────────────────────────────────────────────────────────────────────

  function render() {
    if (!containerEl) return;
    const hasBoundary = !!(state.boundaryCoords && state.boundaryCoords.length >= 3);
    const hasSpine = !!(state.spineCoords && state.spineCoords.length >= 2);
    const hasSection = !!(state.sectionCoords && state.sectionCoords.length >= 2);
    const hasLines = lines.length > 0;
    const calc = previewCalc(state.swathAngle, state.minDepth, state.overlap);
    const s = stats;
    const isSpine = state.mode === "spine";
    const isSection = state.mode === "section";
    const canGenerate = hasBoundary && (!isSpine || hasSpine) && (!isSection || hasSection);

    containerEl.innerHTML = `
<div style="padding:10px 8px;display:flex;flex-direction:column;gap:10px;">

  <div class="card" style="padding:8px 10px;">
    <div class="lm-title" style="font-size:12px;margin-bottom:6px;">Survey Boundary</div>
    <div class="row" style="gap:6px;flex-wrap:wrap;">
      <button id="mbslp-draw" class="btn${drawingBoundary ? " primary" : ""}" style="font-size:11px;">
        ${drawingBoundary ? "⬛ Finish / Esc" : "✏ Draw Boundary"}
      </button>
      <label class="btn" style="font-size:11px;cursor:pointer;margin:0;" title="Load KMZ, KML, or GeoJSON boundary">
        📂 Import
        <input type="file" id="mbslp-import-file" accept=".kmz,.kml,.geojson,.json" style="display:none;" />
      </label>
      ${hasBoundary && !drawingBoundary ? `<button id="mbslp-clear-bnd" class="btn" style="font-size:11px;color:#f87171;border-color:#f8717144;">✕ Clear</button>` : ""}
    </div>
    <div class="small" style="margin-top:5px;color:${drawingBoundary ? "#f59e0b" : hasBoundary ? "#22c55e" : "var(--muted)"};">
      ${drawingBoundary
        ? `Click to add vertices <span id="mbslp-draw-count" style="font-weight:600;">${drawVertices.length ? drawVertices.length + " pts" : ""}</span>`
        : hasBoundary ? `✓ ${state.boundaryCoords.length} vertices` : "No boundary drawn."}
    </div>
    <div style="margin-top:6px;padding-top:6px;border-top:1px solid var(--border);">
      <button id="mbslp-contour" class="btn${contourDrawn ? " primary" : ""}" style="font-size:11px;" ${!hasBoundary ? "disabled" : ""}>
        ${contourDrawn ? `✕ Clear ${state.minDepth}m Contour` : `〰 ${state.minDepth}m Contour`}
      </button>
    </div>
  </div>

  <div class="card" style="padding:8px 10px;">
    <div class="lm-title" style="font-size:12px;margin-bottom:6px;">Line Mode</div>
    <div class="row" style="gap:0;border:1px solid var(--border);border-radius:5px;overflow:hidden;">
      <button id="mbslp-mode-azimuth" class="btn" style="flex:1;border-radius:0;border:none;font-size:11px;${!isSpine && !isSection ? "background:var(--accent-bg,#1e3a5f);color:#38bdf8;" : ""}">Azimuth</button>
      <button id="mbslp-mode-spine" class="btn" style="flex:1;border-radius:0;border:none;border-left:1px solid var(--border);font-size:11px;${isSpine ? "background:var(--accent-bg,#1e3a5f);color:#a78bfa;" : ""}">Spine</button>
      <button id="mbslp-mode-section" class="btn" style="flex:1;border-radius:0;border:none;border-left:1px solid var(--border);font-size:11px;${isSection ? "background:var(--accent-bg,#1e3a5f);color:#fb7185;" : ""}">Section</button>
    </div>
  </div>

  ${!isSpine && !isSection ? `
  <div class="card" style="padding:8px 10px;">
    <div class="lm-title" style="font-size:12px;margin-bottom:8px;">Parameters</div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px 12px;">
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Line Azimuth${drawingAzimuth ? ` <span id="mbslp-az-live" style="color:#38bdf8;font-weight:600;font-size:10px;">click pt 1…</span>` : ""}
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-azimuth" type="number" min="0" max="359" step="1" value="${state.azimuth}" style="width:52px;" />
          <span class="small" style="color:var(--muted);">°</span>
          <span class="small" style="color:#38bdf8;font-size:10px;font-weight:600;">${azimuthLabel(state.azimuth)}</span>
          <button id="mbslp-draw-az" class="btn${drawingAzimuth ? " primary" : ""}" style="font-size:10px;padding:1px 5px;line-height:1.4;" title="Draw azimuth on map">↗</button>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Full Swath Angle
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-swath" type="number" min="1" max="179" step="1" value="${state.swathAngle}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">°</span>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Overlap
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-overlap" type="number" min="0" max="95" step="1" value="${state.overlap}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">%</span>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;" title="Minimum depth for swath calc; land positions skipped">
        Min Depth Floor
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-mindepth" type="number" min="0.5" max="500" step="0.5" value="${state.minDepth}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">m</span>
        </div>
      </label>
    </div>
    <div style="margin-top:7px;padding-top:6px;border-top:1px solid var(--border);display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
      <div class="small" style="flex:1;color:var(--muted);font-size:10px;line-height:1.5;">
        At ${state.minDepth}m → swath <strong style="color:#e2e8f0;">${calc.swath}m</strong> → spacing <strong style="color:#e2e8f0;">${calc.spacing}m</strong>
        &nbsp;·&nbsp; land skipped &nbsp;·&nbsp; lines at ${state.azimuth}° (${azimuthLabel(state.azimuth)})
      </div>
      <button id="mbslp-auto-orient" class="btn" style="font-size:10px;padding:2px 7px;white-space:nowrap;" title="Sample ETOPO inside boundary and set azimuth along-isobath">🧭 Auto</button>
    </div>
  </div>
  ` : isSection ? `
  <div class="card" style="padding:8px 10px;">
    <div class="lm-title" style="font-size:12px;margin-bottom:5px;">Section Line</div>
    <div class="small" style="color:var(--muted);font-size:10px;margin-bottom:7px;line-height:1.5;">Draw the upslope/downslope section. ETOPO is sampled along this line, then survey lines are generated perpendicular to it at depth-based intervals.</div>
    <div class="row" style="gap:6px;flex-wrap:wrap;">
      <button id="mbslp-draw-section" class="btn${drawingSection ? " primary" : ""}" style="font-size:11px;">
        ${drawingSection ? "⬛ Finish / Esc" : "✏ Draw Section"}
      </button>
      ${hasSection && !drawingSection ? `<button id="mbslp-clear-section" class="btn" style="font-size:11px;color:#f87171;border-color:#f8717144;">✕ Clear Section</button>` : ""}
    </div>
    <div class="small" style="margin-top:5px;color:${drawingSection ? "#fb7185" : hasSection ? "#22c55e" : "var(--muted)"};">
      ${drawingSection
        ? `Click to add points <span id="mbslp-section-count" style="font-weight:600;">${sectionVertices.length ? sectionVertices.length + " pts" : ""}</span>`
        : hasSection ? `✓ ${state.sectionCoords.length} vertices` : "No section drawn."}
    </div>
    <div style="margin-top:8px;padding-top:6px;border-top:1px solid var(--border);display:grid;grid-template-columns:1fr 1fr;gap:8px 12px;">
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Sample Interval
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-section-sample" type="number" min="1" max="5000" step="1" value="${state.sectionSampleSpacing}" style="width:64px;" />
          <span class="small" style="color:var(--muted);">m</span>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Full Swath Angle
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-swath" type="number" min="1" max="179" step="1" value="${state.swathAngle}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">°</span>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Overlap
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-overlap" type="number" min="0" max="95" step="1" value="${state.overlap}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">%</span>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Min Depth Floor
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-mindepth" type="number" min="0.5" max="500" step="0.5" value="${state.minDepth}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">m</span>
        </div>
      </label>
    </div>
    <div class="small" style="margin-top:6px;color:var(--muted);font-size:10px;">
      At ${state.minDepth}m → swath <strong style="color:#e2e8f0;">${calc.swath}m</strong> → spacing <strong style="color:#e2e8f0;">${calc.spacing}m</strong> &nbsp;·&nbsp; lines perpendicular to section
    </div>
  </div>
  ` : `
  <div class="card" style="padding:8px 10px;">
    <div class="lm-title" style="font-size:12px;margin-bottom:5px;">Spine Line</div>
    <div class="small" style="color:var(--muted);font-size:10px;margin-bottom:7px;line-height:1.5;">Draw a curved reference through the survey area. Lines generate perpendicular to the spine's local direction at each step, following its curvature.</div>
    <div class="row" style="gap:6px;flex-wrap:wrap;">
      <button id="mbslp-draw-spine" class="btn${drawingSpine ? " primary" : ""}" style="font-size:11px;">
        ${drawingSpine ? "⬛ Finish / Esc" : "✏ Draw Spine"}
      </button>
      ${hasSpine && !drawingSpine ? `<button id="mbslp-clear-spine" class="btn" style="font-size:11px;color:#f87171;border-color:#f8717144;">✕ Clear Spine</button>` : ""}
    </div>
    <div class="small" style="margin-top:5px;color:${drawingSpine ? "#a78bfa" : hasSpine ? "#22c55e" : "var(--muted)"};">
      ${drawingSpine
        ? `Click to add points <span id="mbslp-spine-count" style="font-weight:600;">${spineVertices.length ? spineVertices.length + " pts" : ""}</span>`
        : hasSpine ? `✓ ${state.spineCoords.length} vertices` : "No spine drawn."}
    </div>
    <div style="margin-top:8px;padding-top:6px;border-top:1px solid var(--border);display:grid;grid-template-columns:1fr 1fr;gap:8px 12px;">
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Full Swath Angle
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-swath" type="number" min="1" max="179" step="1" value="${state.swathAngle}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">°</span>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Overlap
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-overlap" type="number" min="0" max="95" step="1" value="${state.overlap}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">%</span>
        </div>
      </label>
      <label class="small" style="display:flex;flex-direction:column;gap:3px;">
        Min Depth Floor
        <div class="row" style="gap:4px;align-items:center;">
          <input id="mbslp-mindepth" type="number" min="0.5" max="500" step="0.5" value="${state.minDepth}" style="width:60px;" />
          <span class="small" style="color:var(--muted);">m</span>
        </div>
      </label>
    </div>
    <div class="small" style="margin-top:6px;color:var(--muted);font-size:10px;">
      At ${state.minDepth}m → swath <strong style="color:#e2e8f0;">${calc.swath}m</strong> → spacing <strong style="color:#e2e8f0;">${calc.spacing}m</strong> &nbsp;·&nbsp; land skipped
    </div>
  </div>
  `}

  <button id="mbslp-generate" class="btn primary"
    ${!canGenerate || (generating && !generateAbort) ? "disabled" : ""}
    style="font-size:12px;padding:6px 10px;">
    ${generating ? "⏹ Cancel" : "⚡ Generate Lines"}
  </button>

  <div id="mbslp-status" class="small" style="min-height:14px;color:var(--muted);">${esc(status)}</div>

  ${hasLines && s ? `
  <div class="card" style="padding:8px 10px;">
    <div class="lm-title" style="font-size:12px;margin-bottom:7px;">Results</div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:3px 14px;font-size:11px;line-height:1.6;">
      <div><span style="color:var(--muted);">Lines</span> <strong>${s.count}</strong></div>
      <div><span style="color:var(--muted);">Total length</span> <strong>${s.totalKm} km</strong></div>
      <div><span style="color:var(--muted);">Avg spacing</span> <strong>${s.avgSpacingM} m</strong></div>
      <div><span style="color:var(--muted);">Min/Max spacing</span> <strong>${s.minSpacingM}–${s.maxSpacingM} m</strong></div>
      <div><span style="color:var(--muted);">Shallowest</span> <strong>${s.minDepthM} m</strong></div>
      <div><span style="color:var(--muted);">Deepest</span> <strong>${s.maxDepthM} m</strong></div>
    </div>
    <div class="row" style="gap:6px;margin-top:8px;flex-wrap:wrap;">
      <button id="mbslp-ex-geojson" class="btn" style="font-size:11px;">GeoJSON</button>
      <button id="mbslp-ex-hypack" class="btn" style="font-size:11px;">HYPACK</button>
      <button id="mbslp-ex-kmz" class="btn" style="font-size:11px;">KMZ</button>
      <button id="mbslp-clear-lines" class="btn" style="font-size:11px;color:#f87171;border-color:#f8717144;">✕ Clear</button>
    </div>
  </div>
  ` : ""}

</div>`;

    bindControls();
  }

  function bindControls() {
    if (!containerEl) return;

    containerEl.querySelector("#mbslp-draw")?.addEventListener("click", () => {
      if (drawingBoundary) finishDraw(); else startDrawBoundary();
    });
    containerEl.querySelector("#mbslp-import-file")?.addEventListener("change", (e) => {
      const file = e.target?.files?.[0];
      if (file) importBoundaryFile(file);
      e.target.value = "";
    });
    containerEl.querySelector("#mbslp-clear-bnd")?.addEventListener("click", () => {
      state.boundaryCoords = null; lines = []; stats = null; status = "";
      boundaryLayer.clearLayers(); linesLayer.clearLayers();
      contourLayer.clearLayers(); contourDrawn = false;
      waterGrid = null;
      persist(); render();
    });
    containerEl.querySelector("#mbslp-contour")?.addEventListener("click", () => draw0mContour());

    containerEl.querySelector("#mbslp-mode-azimuth")?.addEventListener("click", () => {
      if (state.mode === "azimuth") return;
      if (drawingSpine) stopSpineDraw("cancelled");
      if (drawingSection) stopSectionDraw("cancelled");
      state.mode = "azimuth"; lines = []; stats = null; status = "";
      linesLayer.clearLayers(); persist(); render();
    });
    containerEl.querySelector("#mbslp-mode-spine")?.addEventListener("click", () => {
      if (state.mode === "spine") return;
      if (drawingAzimuth) stopDrawAzimuth("cancelled");
      if (drawingSection) stopSectionDraw("cancelled");
      state.mode = "spine"; lines = []; stats = null; status = "";
      linesLayer.clearLayers(); persist(); render();
    });
    containerEl.querySelector("#mbslp-mode-section")?.addEventListener("click", () => {
      if (state.mode === "section") return;
      if (drawingAzimuth) stopDrawAzimuth("cancelled");
      if (drawingSpine) stopSpineDraw("cancelled");
      state.mode = "section"; lines = []; stats = null; status = "";
      linesLayer.clearLayers(); persist(); render();
    });

    containerEl.querySelector("#mbslp-draw-az")?.addEventListener("click", () => {
      if (drawingAzimuth) stopDrawAzimuth("cancelled"); else startDrawAzimuth();
    });
    containerEl.querySelector("#mbslp-auto-orient")?.addEventListener("click", () => autoOrientFromBathymetry());

    containerEl.querySelector("#mbslp-draw-spine")?.addEventListener("click", () => {
      if (drawingSpine) finishSpineDraw(); else startDrawSpine();
    });
    containerEl.querySelector("#mbslp-clear-spine")?.addEventListener("click", () => {
      state.spineCoords = null; lines = []; stats = null; status = "";
      spineLayer.clearLayers(); linesLayer.clearLayers(); persist(); render();
    });

    containerEl.querySelector("#mbslp-draw-section")?.addEventListener("click", () => {
      if (drawingSection) finishSectionDraw(); else startDrawSection();
    });
    containerEl.querySelector("#mbslp-clear-section")?.addEventListener("click", () => {
      state.sectionCoords = null; lines = []; stats = null; status = "";
      sectionLayer.clearLayers(); linesLayer.clearLayers(); persist(); render();
    });

    containerEl.querySelector("#mbslp-generate")?.addEventListener("click", () => {
      if (generating) generateAbort = true; else generateLines();
    });
    containerEl.querySelector("#mbslp-ex-geojson")?.addEventListener("click", exportGeoJson);
    containerEl.querySelector("#mbslp-ex-hypack")?.addEventListener("click", exportHypack);
    containerEl.querySelector("#mbslp-ex-kmz")?.addEventListener("click", () => exportKmz().catch(console.error));
    containerEl.querySelector("#mbslp-clear-lines")?.addEventListener("click", () => {
      lines = []; stats = null; status = ""; linesLayer.clearLayers(); render();
    });

    const numInput = (id, setter) => {
      const el = containerEl.querySelector(id);
      if (!el) return;
      el.addEventListener("change", () => { setter(Number(el.value)); persist(); render(); });
    };
    numInput("#mbslp-azimuth", (v) => { state.azimuth = Math.max(0, Math.min(359, v || 0)); });
    numInput("#mbslp-swath", (v) => { state.swathAngle = Math.max(1, Math.min(179, v || 120)); });
    numInput("#mbslp-overlap", (v) => { state.overlap = Math.max(0, Math.min(95, v || 10)); });
    numInput("#mbslp-mindepth", (v) => {
      state.minDepth = Math.max(0.5, v || 5);
      contourLayer.clearLayers(); contourDrawn = false; waterGrid = null;
    });
    numInput("#mbslp-section-sample", (v) => { state.sectionSampleSpacing = Math.max(1, Math.min(5000, v || 25)); });
  }

  function mount(el) {
    containerEl = el;
    renderBoundaryOnMap(); renderSpineOnMap(); renderSectionOnMap(); renderLinesOnMap(); render();
  }

  function destroy() {
    stopDraw("destroy"); stopDrawAzimuth("destroy"); stopSpineDraw("destroy"); stopSectionDraw("destroy");
    try { boundaryLayer.clearLayers(); map.removeLayer(boundaryLayer); } catch {}
    try { spineLayer.clearLayers(); map.removeLayer(spineLayer); } catch {}
    try { sectionLayer.clearLayers(); map.removeLayer(sectionLayer); } catch {}
    try { linesLayer.clearLayers(); map.removeLayer(linesLayer); } catch {}
    try { drawLayer.clearLayers(); map.removeLayer(drawLayer); } catch {}
    try { contourLayer.clearLayers(); map.removeLayer(contourLayer); } catch {}
    containerEl = null;
  }

  return { mount, destroy };
}

export const mbswathPlannerTool = {
  id: "mbswath-planner",
  name: "MBSWATH Planner",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy(); this._rt = null; },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
};
